import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Container, Typography } from '@mui/material';
import PageTitle from '../components/coreLayout/PageTitle';
import Seo from '../components/coreLayout/Seo';

interface FaqItem {
    readonly question: string;
    readonly answer: string;
}

interface FaqCategory {
    readonly category: string;
    readonly questions: readonly FaqItem[];
}

const faqContent: readonly FaqCategory[] = [
    {
        category: 'Booking & Pricing',
        questions: [
            {
                question: 'How much do your scenic flights cost?',
                answer: "Fares vary by tour. Our Reef Hopper scenic flight starts from $289 per adult and $229 per child (ages 3–14), with infants under 2 flying free on a parent's lap. Package tours such as Fly & Snorkel start from $469 per adult and $369 per child (ages 6–14). All fares are per person — see each tour page for current pricing and live availability.",
            },
            {
                question: "What's included in the price?",
                answer: 'Every scenic flight includes a guaranteed window seat, your pre-flight safety briefing and all safety equipment. The Reef Hopper also includes return transfers from selected Cairns hotels. Package tours may add cruises, snorkelling or meals — check the inclusions listed on each tour page.',
            },
            {
                question: 'Do you offer group or charter bookings?',
                answer: "Yes. For groups of 10 or more, our reservations team can arrange group rates or charter one, two or three aircraft to suit your party. Get in touch and we'll tailor it for you.",
            },
        ],
    },
    {
        category: 'Cancellations & Changes',
        questions: [
            {
                question: "What's your cancellation policy?",
                answer: "Cancel more than 24 hours before departure and you'll receive a full refund with no fee (direct bookings). Within 24 hours of departure, cancellations and changes aren't possible and a 100% fee applies. If you booked through a travel agent, their terms apply and they may charge an admin fee (typically 10–25%).",
            },
            {
                question: 'Can I reschedule my flight?',
                answer: "Changes aren't possible within 24 hours of departure. If your flight is more than 24 hours away, you can cancel for a full refund and rebook another time, subject to availability.",
            },
            {
                question: 'What happens if my flight is cancelled due to weather?',
                answer: 'If the pilot cancels for weather, you can rebook free of charge or take a full refund. Weather-cancellation refunds are always 100% for direct bookings; most agents also refund in full, though some retain a small admin fee.',
            },
        ],
    },
    {
        category: 'Weather & Flight Operations',
        questions: [
            {
                question: 'Are flights affected by the weather?',
                answer: "All flights operate at the pilot's discretion and depend on conditions. Cairns gets passing tropical showers, so minor delays of 30–60 minutes can happen. If the pilot decides to cancel, you can rebook free of charge or request a full refund.",
            },
            {
                question: "What happens if there's a weather delay?",
                answer: "For a short delay rather than a cancellation, the pilot may push departure back 30–60 minutes. If you're already at our terminal you're welcome to wait there; if it's earlier in the day, we'll contact you with updated pick-up or check-in times.",
            },
        ],
    },
    {
        category: 'Age, Weight & Ability',
        questions: [
            {
                question: 'What are the age requirements?',
                answer: "Infants aged 2 and under fly free on the Reef Hopper (on a parent's lap). Children aged 3–14 need their own seat at the child fare. The Fly & Snorkel tour has a minimum age of 6 years.",
            },
            {
                question: 'Is there a weight limit?',
                answer: 'Yes — the maximum individual passenger weight is 125 kg. Please declare your accurate weight when booking so we can balance the aircraft correctly.',
            },
            {
                question: 'Is the Fly & Snorkel suitable for non-swimmers?',
                answer: "No. The snorkelling component requires swimming ability and isn't suitable for non-swimmers or guests with limited mobility. The scenic flight on its own has no swimming requirement.",
            },
        ],
    },
    {
        category: 'Health & Safety',
        questions: [
            {
                question: 'Are there any health or mobility considerations?',
                answer: "Our small fixed-wing aircraft take a little more effort to board and exit than a commercial airliner. If you have limited mobility, a recent surgery or injury, or are in advanced pregnancy, please consider this carefully — and check with your doctor if you're unsure before booking.",
            },
            {
                question: 'Can you cater to dietary requirements?',
                answer: "Our package partners cater to all dietary requirements. We don't normally need to specifically mention common dietary requirements, but it never hurts to make a note on the booking just to be sure.",
            },
        ],
    },
    {
        category: 'On the Day',
        questions: [
            {
                question: 'When should I arrive?',
                answer: 'Please arrive at least 30 minutes before your scheduled departure. We run our own passenger terminal at Cairns Airport for check-in, facilities and luggage lockers.',
            },
            {
                question: 'What should I wear and bring?',
                answer: "Comfortable tropical clothing and enclosed footwear. Bring your phone, camera or GoPro for photos and video — GoPros must be hand-held (not mounted to the aircraft) and selfie sticks aren't permitted. Large bags go in our terminal lockers rather than on board.",
            },
            {
                question: 'Is transport provided?',
                answer: 'Yes — we offer a free courtesy bus to and from Cairns city (subject to availability), and the Reef Hopper includes return transfers from selected Cairns hotels.',
            },
        ],
    },
];

const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqContent.flatMap((cat) =>
        cat.questions.map((q) => ({
            '@type': 'Question',
            name: q.question,
            acceptedAnswer: {
                '@type': 'Answer',
                text: q.answer,
            },
        }))
    ),
};

const faqSx = {
    container: {
        paddingLeft: { xs: '10px', sm: '24px' },
        paddingRight: { xs: '10px', sm: '24px' },
        paddingBottom: { xs: 6, sm: 8 },
    },
    categoryHeading: {
        fontSize: { xs: 20, sm: 24 },
        fontWeight: 600,
        color: 'primary.main',
        mt: { xs: 4, sm: 5 },
        mb: 1.5,
    },
    accordion: {
        boxShadow: 'none',
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: '10px !important',
        mb: 1.25,
        '&:before': { display: 'none' },
        overflow: 'hidden',
    },
    summary: {
        '& .MuiAccordionSummary-content': { my: 1.5 },
        fontWeight: 500,
        fontSize: { xs: 15, sm: 16 },
    },
    answer: {
        fontSize: { xs: 14.5, sm: 16 },
        lineHeight: 1.65,
        opacity: 0.82,
    },
} as const;

export default function Faq() {
    return (
        <>
            <Seo
                title="Scenic Flight FAQs | Cairns & Great Barrier Reef Tours | GSL Aviation"
                description="Answers to common questions about GSL Aviation's Cairns scenic flights and Great Barrier Reef tours — pricing, cancellations, weather, age limits, what to bring and more."
                path="/faq"
                jsonLd={faqJsonLd}
            />
            <Container maxWidth="md" sx={faqSx.container}>
                <PageTitle
                    title="Frequently Asked Questions"
                    tagline="Everything you need to know before your Cairns scenic flight"
                    compact
                />

                {faqContent.map((cat) => (
                    <Box key={cat.category}>
                        <Typography component="h2" sx={faqSx.categoryHeading}>
                            {cat.category}
                        </Typography>
                        {cat.questions.map((item) => (
                            <Accordion key={item.question} disableGutters sx={faqSx.accordion}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={faqSx.summary}>
                                    {item.question}
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Typography sx={faqSx.answer}>{item.answer}</Typography>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Box>
                ))}
            </Container>
        </>
    );
}
